import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button } from "./Button";
import { ErrorState } from "./State";
import { t } from "../../lib/i18n";

/**
 * Minimal localized crash fallback — title + "reload" button. Shared by the
 * two error-catching mechanisms in this app so a user sees the same screen
 * regardless of which one caught the error:
 *  - `ErrorBoundary` below (class component, catches renders outside the
 *    router — e.g. a provider throwing).
 *  - `RouteErrorElement` in `router.tsx` (React Router's `errorElement`,
 *    catches renders inside a route's own element tree — see the note on
 *    `ErrorBoundary` below for why both are needed).
 *
 * Reload is a full `location.reload()` rather than a "try again" state
 * reset: the error may have left React Query cache / router history in an
 * inconsistent state, and a hard reload is the only recovery guaranteed to
 * work regardless of what threw.
 */
export function CrashFallback() {
  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <ErrorState
        titleKey="errorBoundary.title"
        bodyKey="errorBoundary.body"
        action={
          <Button onClick={() => window.location.reload()}>{t("errorBoundary.reload")}</Button>
        }
      />
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * App-level crash guard (docs/admin/ux-audit.md P2: "No React error
 * boundary — any render exception blanks the SPA"). Mounted once in
 * App.tsx around the provider tree so a render exception anywhere below it
 * shows `CrashFallback` instead of an unrecoverable blank page.
 *
 * IMPORTANT: `createBrowserRouter` gives every route an implicit default
 * `errorElement` even when none is configured — React Router's own
 * (unstyled, English-only) crash screen intercepts a render error thrown
 * inside a route's element tree before it would ever reach this boundary,
 * because RouterProvider's internal per-route error handling sits *between*
 * the thrown error and this component. Confirmed live: a throw inside
 * LoginPage rendered React Router's default "Unexpected Application Error!"
 * page, not this one. That's why `router.tsx` also attaches `errorElement`
 * (`RouteErrorElement`, rendering this same `CrashFallback`) to every route
 * — this class boundary alone only protects renders that happen outside the
 * router's route tree (e.g. AuthProvider/ToastProvider/I18nProvider
 * throwing). Both are required for full coverage; neither alone is enough.
 *
 * Also note: an error boundary can only catch errors thrown by its
 * children's render — not by the component that renders the boundary
 * itself. It is mounted as high as App.tsx's own render allows (wrapping
 * every provider), but a throw from `App()`'s own body (e.g. from
 * `useLocale()`) is outside what any boundary placed inside `App()` can
 * catch.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Unhandled render error", error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return <CrashFallback />;
  }
}
